import React, { useEffect, useState } from "react";
import api, { API, buildWaLink } from "../lib/api";
import { sendWaSmart } from "../lib/wa";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Plus, Pencil, Trash2, MessageCircle, Search, MapPin, Upload, Download, ClipboardPlus } from "lucide-react";
import { toast } from "sonner";
import CustomerFormModal from "../components/CustomerFormModal";

const empty = { name: "", phone: "", address: "", city: "", maps_link: "", latitude: "", longitude: "", notes: "" };

const Customers = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [initial, setInitial] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const createOrderFor = (c) => navigate(`/orders?new=1&customer_id=${encodeURIComponent(c.id)}`);

  const load = async () => {
    const { data } = await api.get("/customers", { params: { search } });
    setList(data);
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const openCreate = () => { setInitial(empty); setEditingId(null); setModal(true); };
  const openEdit = (c) => {
    setInitial({
      name: c.name || "", phone: c.phone || "", address: c.address || "", city: c.city || "",
      maps_link: c.maps_link || "",
      latitude: c.latitude ?? "", longitude: c.longitude ?? "",
      notes: c.notes || "",
    });
    setEditingId(c.id); setModal(true);
  };

  const submit = async (form) => {
    const payload = {
      ...form,
      latitude: form.latitude === "" ? null : Number(form.latitude),
      longitude: form.longitude === "" ? null : Number(form.longitude),
    };
    try {
      if (editingId) {
        await api.put(`/customers/${editingId}`, payload);
        toast.success("Pelanggan diperbarui");
      } else {
        await api.post("/customers", payload);
        toast.success("Pelanggan ditambahkan");
      }
      setModal(false); load();
    } catch (err) {
      toast.error("Gagal menyimpan pelanggan");
    }
  };

  const remove = async () => {
    if (!confirmDel) return;
    await api.delete(`/customers/${confirmDel.id}`);
    setConfirmDel(null);
    toast.success("Pelanggan dihapus");
    load();
  };

  const sendWa = (c) => {
    const text = `Halo KK ${c.name}, ini pesan dari tim kami. Apakah ada yang bisa kami bantu?`;
    sendWaSmart({ phone: c.phone, text, name: c.name || "" });
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem("om_token");
    window.open(`${API}/customers/template.csv`, "_blank");
    // Authorization via fetch+blob since template requires auth header
    fetch(`${API}/customers/template.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = "template_pelanggan.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} pelanggan diimpor${data.errors.length ? ` (${data.errors.length} error)` : ""}`);
      load();
    } catch { toast.error("Gagal import CSV"); }
    e.target.value = "";
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <div className="label-cap mb-2">Direktori</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black tracking-tight">Pelanggan</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={downloadTemplate} data-testid="customer-csv-template" className="text-xs font-bold uppercase tracking-wider px-3 py-2.5 border border-[#E4E4E7] hover:bg-[#F4F4F5] flex items-center gap-2 min-h-[44px]">
            <Download size={14} /> <span className="hidden sm:inline">Template CSV</span><span className="sm:hidden">Template</span>
          </button>
          <label className="text-xs font-bold uppercase tracking-wider px-3 py-2.5 border border-[#E4E4E7] hover:bg-[#F4F4F5] flex items-center gap-2 cursor-pointer min-h-[44px]">
            <Upload size={14} /> Import
            <input type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="customer-csv-upload" />
          </label>
          <button onClick={openCreate} data-testid="add-customer-button" className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2 min-h-[44px] flex-1 sm:flex-initial justify-center">
            <Plus size={14} /> <span className="hidden sm:inline">Tambah Pelanggan</span><span className="sm:hidden">Tambah</span>
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7]">
        <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-2">
          <Search size={14} className="text-[#52525B]" />
          <input
            data-testid="customer-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama, telepon, atau alamat…"
            className="flex-1 outline-none text-sm bg-transparent"
          />
        </div>
        <table className="w-full hidden md:table">
          <thead>
            <tr className="border-b border-[#E4E4E7]">
              <th className="text-left label-cap px-4 py-3">Nama</th>
              <th className="text-left label-cap px-4 py-3">Telepon</th>
              <th className="text-left label-cap px-4 py-3">Alamat</th>
              <th className="text-left label-cap px-4 py-3">Kota</th>
              <th className="text-left label-cap px-4 py-3">Lokasi</th>
              <th className="text-right label-cap px-4 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan="6" className="p-8 text-center text-sm text-[#52525B]">Belum ada pelanggan.</td></tr>
            )}
            {list.map((c) => {
              const mapsHref = c.maps_link || (c.latitude && c.longitude ? `https://maps.google.com/?q=${c.latitude},${c.longitude}` : null);
              return (
                <tr key={c.id} className="border-b border-[#E4E4E7] row-hover" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3 text-sm font-bold">{c.name}</td>
                  <td className="px-4 py-3 text-sm font-mono">{c.phone}</td>
                  <td className="px-4 py-3 text-sm text-[#52525B] max-w-xs truncate">{c.address}</td>
                  <td className="px-4 py-3 text-sm text-[#52525B]">{c.city || "—"}</td>
                  <td className="px-4 py-3 text-sm">
                    {mapsHref ? (
                      <a href={mapsHref} target="_blank" rel="noreferrer" className="text-[#002FA7] inline-flex items-center gap-1 hover:underline" data-testid={`customer-map-${c.id}`}>
                        <MapPin size={12} /> Buka
                      </a>
                    ) : <span className="text-[#52525B] text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => sendWa(c)} title="Kirim WhatsApp" data-testid={`customer-wa-${c.id}`} className="p-2 hover:bg-[#059669]/10 text-[#059669] border border-[#E4E4E7]"><MessageCircle size={14} /></button>
                      <button onClick={() => createOrderFor(c)} title="Buat Order untuk pelanggan ini" data-testid={`customer-create-order-${c.id}`} className="p-2 hover:bg-[#002FA7]/10 text-[#002FA7] border border-[#E4E4E7]"><ClipboardPlus size={14} /></button>
                      <button onClick={() => openEdit(c)} title="Edit" data-testid={`customer-edit-${c.id}`} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7]"><Pencil size={14} /></button>
                      <button onClick={() => setConfirmDel(c)} title="Hapus" data-testid={`customer-delete-${c.id}`} className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Mobile cards */}
        <div className="md:hidden">
          {list.length === 0 && (
            <div className="p-8 text-center text-sm text-[#52525B]">Belum ada pelanggan.</div>
          )}
          {list.map((c) => {
            const mapsHref = c.maps_link || (c.latitude && c.longitude ? `https://maps.google.com/?q=${c.latitude},${c.longitude}` : null);
            return (
              <div key={c.id} className="border-b border-[#E4E4E7] p-4 space-y-2" data-testid={`customer-card-${c.id}`}>
                <div>
                  <div className="font-bold text-base text-[#111113]">{c.name}</div>
                  <div className="text-xs font-mono text-[#52525B] mt-0.5">{c.phone}</div>
                </div>
                {(c.address || c.city) && <div className="text-xs text-[#52525B]">{[c.address, c.city].filter(Boolean).join(", ")}</div>}
                <div className="flex flex-wrap gap-2 pt-2">
                  <a href={mapsHref || `https://maps.google.com/?q=${encodeURIComponent(c.address || c.name)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-3 h-10 border border-[#E4E4E7] text-[#002FA7]" data-testid={`customer-map-m-${c.id}`}>
                    <MapPin size={14} /> Maps
                  </a>
                  <button onClick={() => sendWa(c)} className="inline-flex items-center gap-1 text-xs px-3 h-10 bg-[#059669] text-white" data-testid={`customer-wa-m-${c.id}`}>
                    <MessageCircle size={14} /> WA
                  </button>
                  <button onClick={() => createOrderFor(c)} className="inline-flex items-center gap-1 text-xs px-3 h-10 bg-[#002FA7] text-white" data-testid={`customer-create-order-m-${c.id}`}>
                    <ClipboardPlus size={14} /> Order
                  </button>
                  <button onClick={() => openEdit(c)} className="inline-flex items-center justify-center h-10 w-10 border border-[#E4E4E7]" data-testid={`customer-edit-m-${c.id}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => setConfirmDel(c)} className="inline-flex items-center justify-center h-10 w-10 border border-[#DC2626]/30 text-[#DC2626]" data-testid={`customer-delete-m-${c.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modal && (
        <CustomerFormModal initial={initial} editingId={editingId} onClose={() => setModal(false)} onSubmit={submit} />
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-sm w-full p-6" data-testid="confirm-delete-customer">
            <h3 className="font-heading text-lg font-bold mb-2">Hapus Pelanggan?</h3>
            <p className="text-sm text-[#52525B] mb-6">"{confirmDel.name}" akan dihapus permanen.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={remove} data-testid="confirm-delete-customer-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#DC2626] text-white">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Customers;
